import { generatePath, Link } from 'react-router'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'

import { URL_PAGE_CATALOG_1ST_LVL } from '@shared/contants'
import { CATEGORY_DEFAULT_ICON_PATH } from '@shared/contants/product'
import { cn } from '@shared/lib/cn'

import { useCategorySlugs } from './parts/useCategorySlugs'

import styles from './LandingCategoryLinksBlock.module.scss'

/*
  category_links: curated shortcuts into catalog categories, addressed by
  categoryId. Ids are resolved to catalog slugs (see useCategorySlugs); items
  whose category is unavailable for the current shop are dropped, and the section
  disappears when nothing is left.

  Note: the school event landing moved these shortcuts into a `menu` block, but
  the block type stays supported — it is part of the landing contract.
*/
interface CategoryLinkPayloadItem {
  label?: string;
  categoryId?: string | number;
  image?: string;
}

interface CategoryLinksPayload {
  title?: string;
  name?: string;
  layout?: string;
  items?: CategoryLinkPayloadItem[];
}

export default function LandingCategoryLinksBlock ({ payload, shopId }: LandingBlockProps) {
  const { title, name, items } = (payload ?? {}) as CategoryLinksPayload

  const payloadItems = (items ?? []).filter((item) => item?.categoryId != null)
  const categoryIds = payloadItems.map((item) => String(item.categoryId))

  const { categoriesById, loading } = useCategorySlugs(categoryIds, shopId)

  const links = payloadItems
    .map((item) => {
      const category = categoriesById.get(String(item.categoryId))

      if (!category?.slug) {
        return null
      }

      return {
        key: String(item.categoryId),
        label: item.label || category.name || '',
        image: item.image || category.iconUrl || '',
        href: generatePath(URL_PAGE_CATALOG_1ST_LVL, { categorySlug: category.slug }),
      }
    })
    .filter((link): link is NonNullable<typeof link> => Boolean(link))

  const heading = title || name

  if (loading || links.length === 0) {
    return null
  }

  return (
    <section className={styles.root}>
      {heading && <h2 className={styles.root__title}>{heading}</h2>}

      <Swiper
        className={styles.root__slider}
        slidesPerView="auto"
        spaceBetween={12}
        modules={[ Navigation ]}
        navigation
      >
        {links.map((link) => (
          <SwiperSlide className={styles.root__slide} key={link.key}>
            <Link className={styles.item} to={link.href}>
              <span className={styles.item__media}>
                <img
                  className={cn(styles.item__image, {
                    [styles['item__image--plug']]: !link.image,
                  })}
                  src={link.image || CATEGORY_DEFAULT_ICON_PATH}
                  alt=""
                  loading="lazy"
                />
              </span>
              <span className={styles.item__label}>{link.label}</span>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}
